import type { Dispatch, ReactNode, SetStateAction } from "react";

import type { RejectedRequest } from "@shared/utils";

import type { FlowNavigation } from "./navigation";
import type { FlowStep } from "./steps";

import {
	createContext,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { exhaustiveSwitchGuard, useIdempotencyKey } from "@shared/utils";

import { FullscreenLayout } from "../fullscreen-layout";
import { findStepIndex, flattenSteps, getRootSteps, getStepLayout, resolveNextStep } from "./steps";

export type FlowContextValue<Data extends object> = {
	/** Shared data object that persists across flow steps */
	data: Data;
	/** Idempotency key for the flow */
	idempotencyKey: string;
	/** Whether the current step is the first step in the flow */
	isFirstStep: boolean;
	/**
	 * Navigate to the next step in the flow
	 *
	 * The step argument is required if the current step allows multiple
	 * next steps.
	 * */
	next: (step?: string) => void;
	/** Navigate to the previous step in the flow */
	prev: (delta?: number) => void;
	/** Reset the flow data and idempotency key */
	reset: () => void;
	/**
	 * Issue a fresh idempotency key when a submit was rejected outright (4xx), so the corrected
	 * resubmit isn't replayed as the original failure. Leaves the key intact for 5xx and network
	 * errors, where the outcome is unknown and a retry must stay idempotent.
	 */
	rotateIdempotencyKeyIfRejected: (error: RejectedRequest) => void;
	/** Update the shared data object */
	setData: Dispatch<SetStateAction<Data>>;
};

export type FlowAnalyticsEvent =
	| [eventName: "flow_abandoned", properties: { flow: string; step: string }]
	| [eventName: "flow_completed", properties: { flow: string; step: string }]
	| [eventName: "flow_step_back", properties: { flow: string; step: string }]
	| [
			eventName: "flow_step_completed",
			properties: { flow: string; next_step: string; step: string },
	  ];

const FlowContext = createContext<FlowContextValue<object> | null>(null);

/**
 * Hook to access flow context from within a flow step component.
 * Must be used within a FlowContextProvider.
 *
 * @example
 * const { data, next, setData } = useFlow<MyDataType>();
 */
export const useFlow = <Data extends object = object>(): FlowContextValue<Data> => {
	const context = useContext(FlowContext);

	if (!context) {
		throw Error("useFlow can only be used within a FlowContextProvider");
	}
	return context as unknown as FlowContextValue<Data>;
};

type FlowAdapterProviderProps = {
	/**
	 * Base path for the flow (e.g., "/onboarding")
	 * Note: Must not be empty string
	 */
	basePath: string;
	children: ReactNode;
	/** Flow identifier reported on analytics events (e.g. "create_customer") */
	name: string;
	navigation: FlowNavigation;
	/** Called when the user leaves the flow before finishing it */
	onAbort: () => void;
	/** Called when the last step of the flow is completed */
	onComplete: () => void;
	onException?: (reason: string) => void;
	onTrack?: (...args: FlowAnalyticsEvent) => void;
	/**
	 * Array of flow steps defining the navigation sequence.
	 * Each step maps a path to its next step.
	 * @example [
	 *   { "/step1": { next: "/step2" } },
	 *   { "/step2": { next: \["/step3", "/step4"\] } },
	 *   { "/step3": {} },
	 *   { "/step3": {} }
	 * ]
	 *
	 * If a step can reach multiple steps, the `next` field can be an array. The target next step needs to
	 * be specified in the `flow.next('next-step')` callback.
	 */
	steps: readonly FlowStep[];
};

export function FlowAdapterProvider({
	basePath,
	children,
	name,
	navigation,
	onAbort,
	onComplete,
	onException,
	onTrack,
	steps,
}: FlowAdapterProviderProps): ReactNode {
	const [data, setData] = useState<object>({});
	const [startStep, setStartStep] = useState<null | string>(null);
	const { idempotencyKey, rotateIdempotencyKey, rotateIdempotencyKeyIfRejected } =
		useIdempotencyKey();

	// Navigation and the reporting callbacks are read at call time rather than
	// captured, so a host passing fresh objects every render does not rebuild
	// every callback the flow hands to its steps.
	const hostRef = useRef({ navigation, onAbort, onComplete, onException, onTrack });
	useLayoutEffect(() => {
		hostRef.current = { navigation, onAbort, onComplete, onException, onTrack };
	});

	const flattenedSteps = useMemo(() => {
		return flattenSteps(steps);
	}, [steps]);

	const rootSteps = useMemo(() => {
		return getRootSteps(flattenedSteps);
	}, [flattenedSteps]);

	const nestedLocation = useMemo(() => {
		return navigation.currentPath.replace(basePath, "");
	}, [navigation.currentPath, basePath]);

	const currentStepIndex = useMemo(() => {
		return findStepIndex(flattenedSteps, nestedLocation);
	}, [flattenedSteps, nestedLocation]);

	const layout = useMemo(() => {
		return getStepLayout(flattenedSteps, currentStepIndex);
	}, [flattenedSteps, currentStepIndex]);

	const isEntryStep = nestedLocation === startStep;
	const isAtRootStep = rootSteps.includes(nestedLocation);

	/**
	 * Navigate to the next step in the flow.
	 */
	const next = useCallback(
		(step?: string) => {
			const result = resolveNextStep(flattenedSteps, currentStepIndex, step);
			const currentStep = nestedLocation;

			switch (result.type) {
				case "complete": {
					hostRef.current.onTrack?.("flow_completed", {
						flow: name,
						step: currentStep,
					});
					hostRef.current.onComplete();
					return;
				}

				case "error": {
					hostRef.current.onException?.(result.reason);
					return;
				}

				case "noop": {
					return;
				}

				case "step": {
					hostRef.current.onTrack?.("flow_step_completed", {
						flow: name,
						next_step: result.step,
						step: currentStep,
					});
					hostRef.current.navigation.navigate(`${basePath}${result.step}`);
					return;
				}

				default: {
					return exhaustiveSwitchGuard(result);
				}
			}
		},
		[flattenedSteps, currentStepIndex, nestedLocation, basePath, name],
	);

	/**
	 * Navigate to the previous step in the flow.
	 * Only available if not on the first step.
	 */
	const prev = useCallback(
		(delta?: number) => {
			if (currentStepIndex > 0) {
				hostRef.current.onTrack?.("flow_step_back", { flow: name, step: nestedLocation });
				hostRef.current.navigation.back(delta);
			}
		},
		[currentStepIndex, name, nestedLocation],
	);

	const isFirstStep = !(currentStepIndex > 0 && !isEntryStep);

	const reset = useCallback(() => {
		setData({});
		rotateIdempotencyKey();
	}, [rotateIdempotencyKey]);

	// Redirect to first step if current location doesn't match any valid step
	// or if user tries to access a step without starting the flow from a root step
	useLayoutEffect(() => {
		if (flattenedSteps.length === 0) {
			return;
		}

		const firstStep = flattenedSteps[0][0];
		const isValidStep = currentStepIndex !== -1;

		if (!isValidStep) {
			hostRef.current.navigation.navigate(`${basePath}${firstStep}`, { replace: true });
			return;
		}

		if (isAtRootStep) {
			if (!startStep) {
				setStartStep(nestedLocation);
			}
		}

		if (!startStep && !isAtRootStep) {
			hostRef.current.navigation.navigate(`${basePath}${firstStep}`, { replace: true });
		}
	}, [flattenedSteps, currentStepIndex, isAtRootStep, nestedLocation, basePath, startStep]);

	const value: FlowContextValue<object> = useMemo(() => {
		return {
			data,
			idempotencyKey,
			isFirstStep,
			next,
			prev,
			reset,
			rotateIdempotencyKeyIfRejected,
			setData,
		};
	}, [next, data, prev, idempotencyKey, isFirstStep, reset, rotateIdempotencyKeyIfRejected]);

	const onClose = useCallback(() => {
		hostRef.current.onTrack?.("flow_abandoned", { flow: name, step: nestedLocation });
		hostRef.current.onAbort();
	}, [name, nestedLocation]);

	return (
		<FlowContext.Provider value={value}>
			<FullscreenLayout
				layout={layout}
				onClose={onClose}
				prev={prev}
				showBackButton={!isFirstStep}
			>
				{children}
			</FullscreenLayout>
		</FlowContext.Provider>
	);
}
